/* JPEG marker-segment scanner.
 *
 * Why this exists at all: the photo pipeline strips EXIF (including GPS) by
 * decoding to pixels and re-encoding, which structurally cannot carry metadata
 * forward. That is the mechanism. This file is the *check* on the mechanism —
 * it reads the bytes the pipeline is about to hand to the blob store and refuses
 * to proceed if a metadata segment is still there.
 *
 * Defence in depth, not the only line: the server re-encodes every upload as
 * well (control C-36), and that server-side re-encode remains the authoritative
 * strip. This is the client-side belt to the server's braces, and it earns its
 * place because it turns "we believe canvas drops EXIF" into an assertion that
 * can fail loudly in a test and in the field.
 *
 * No dependency, ~60 lines, works on a Uint8Array. Deliberately not a full EXIF
 * parser: we do not need to know what the metadata says, only that there is
 * none.
 */

export interface JpegSegment {
  /** Second byte of the marker, e.g. 0xe1 for APP1. */
  marker: number;
  /** Human name, e.g. `APP1`, `COM`, `SOS`. */
  name: string;
  /** Offset of the 0xFF byte of the marker. */
  offset: number;
  /** Payload length excluding the 2-byte length field, or 0 for markers without one. */
  length: number;
  /** First bytes of the payload, for identifying APP1 as Exif vs XMP. */
  head: string;
}

function markerName(marker: number): string {
  if (marker >= 0xe0 && marker <= 0xef) return `APP${marker - 0xe0}`;
  if (marker >= 0xd0 && marker <= 0xd7) return `RST${marker - 0xd0}`;
  switch (marker) {
    case 0xd8:
      return 'SOI';
    case 0xd9:
      return 'EOI';
    case 0xda:
      return 'SOS';
    case 0xfe:
      return 'COM';
    case 0xdb:
      return 'DQT';
    case 0xc4:
      return 'DHT';
    case 0xc0:
      return 'SOF0';
    case 0xc2:
      return 'SOF2';
    case 0xdd:
      return 'DRI';
    default:
      return `0x${marker.toString(16).padStart(2, '0')}`;
  }
}

/** True if the bytes start with a JPEG SOI. */
export function isJpeg(bytes: Uint8Array): boolean {
  return bytes.length >= 2 && bytes[0] === 0xff && bytes[1] === 0xd8;
}

/**
 * Walk the marker segments of a JPEG, stopping at SOS.
 *
 * Everything metadata-bearing (APPn, COM) is required to appear before the
 * start of scan, so stopping there is correct and avoids having to reason about
 * byte-stuffed entropy-coded data.
 */
export function scanJpegSegments(bytes: Uint8Array): JpegSegment[] {
  const out: JpegSegment[] = [];
  if (!isJpeg(bytes)) return out;
  let i = 2;
  while (i + 1 < bytes.length) {
    if (bytes[i] !== 0xff) {
      // Not sitting on a marker; the file is malformed or we lost sync.
      break;
    }
    let marker = bytes[i + 1] ?? 0;
    // 0xFF padding before a marker is legal.
    let j = i + 1;
    while (marker === 0xff && j + 1 < bytes.length) {
      j += 1;
      marker = bytes[j] ?? 0;
    }
    const name = markerName(marker);
    if (marker === 0xd9 || marker === 0x00) break;
    if (marker === 0xda) {
      out.push({ marker, name, offset: i, length: 0, head: '' });
      break;
    }
    if (marker >= 0xd0 && marker <= 0xd7) {
      out.push({ marker, name, offset: i, length: 0, head: '' });
      i = j + 1;
      continue;
    }
    if (j + 2 >= bytes.length) break;
    const length = ((bytes[j + 1] ?? 0) << 8) | (bytes[j + 2] ?? 0);
    if (length < 2) break;
    const payloadStart = j + 3;
    const payloadEnd = Math.min(payloadStart + length - 2, bytes.length);
    let head = '';
    for (let k = payloadStart; k < Math.min(payloadStart + 32, payloadEnd); k += 1) {
      const b = bytes[k] ?? 0;
      head += b >= 0x20 && b < 0x7f ? String.fromCharCode(b) : '.';
    }
    out.push({ marker, name, offset: i, length: length - 2, head });
    i = payloadEnd;
  }
  return out;
}

/**
 * An APP2 ICC colour profile, which is a rendering instruction and NOT metadata.
 *
 * Its payload begins with the null-terminated signature `ICC_PROFILE`. `head` is built from the
 * first 32 payload bytes with non-printables replaced, and every byte of this signature is
 * printable ASCII, so matching on `head` reads the real payload rather than guessing.
 */
function isIccProfile(segment: JpegSegment): boolean {
  return segment.marker === 0xe2 && segment.head.startsWith('ICC_PROFILE');
}

/**
 * Metadata-bearing segments that must not survive the pipeline.
 *
 * TWO markers are excluded, and both exclusions are deliberate:
 *
 *  · **APP0** — the JFIF density header every encoder writes. No camera, time or location data.
 *  · **APP2 carrying `ICC_PROFILE`** — a colour profile. **Chrome's `canvas.toBlob('image/jpeg')`
 *    writes an sRGB ICC profile into APP2**, so before this exclusion existed EVERY photo taken in
 *    Chrome was rejected with `MetadataNotStrippedError` and the technician was told the photo
 *    "could not be processed safely". An ICC profile is a colour transform: it describes how to
 *    interpret the pixels, and carries no camera identity, timestamp or position. Rejecting it was
 *    a false positive that made photo capture unusable in the browser people actually use.
 *
 *    This did not show up in the tests because they encode with a hand-written baseline JPEG
 *    encoder that emits no ICC segment — the node path and the DOM path produce structurally
 *    different files, and only the node one was exercised. `domRasterPort` says in its own comment
 *    that it is not covered by unit tests, and this is precisely what that gap cost.
 *
 * Everything else in the APPn range stays rejected: EXIF (APP1 `Exif\0\0`), XMP (APP1 with the
 * Adobe namespace URI, which can carry GPS of its own), Photoshop/IPTC (APP13), maker notes, and
 * an APP2 that is NOT an ICC profile. COM is a free-text comment. The exclusion is narrow on
 * purpose — it is keyed to the signature, not to the marker, so an APP2 holding something other
 * than a colour profile is still refused.
 */
export function findMetadataSegments(bytes: Uint8Array): JpegSegment[] {
  return scanJpegSegments(bytes).filter(
    (s) => ((s.marker >= 0xe1 && s.marker <= 0xef) || s.marker === 0xfe) && !isIccProfile(s),
  );
}

/**
 * Drop every metadata-bearing segment from a JPEG, leaving structure and pixels untouched.
 *
 * ⚠️ ADDED 2026-09-04, AND THE REASON IS A SECOND ENCODER FALSE POSITIVE. The ICC note above
 * records the first: Chrome's encoder writes an APP2 colour profile. The second is worse:
 * **WebKit's `canvas.toBlob('image/jpeg')` — desktop AND mobile Safari — writes an APP1
 * `Exif\0\0` segment (~74 B: orientation/resolution the encoder mints itself) and an APP13
 * `Photoshop 3.0/8BIM` segment (~54 B) into EVERY encode.** Measured with Playwright WebKit
 * 26.0, not inferred. Both are squarely in the refused range, so every photo taken in Safari
 * threw `MetadataNotStrippedError` and the technician was told to take it again, forever.
 *
 * The ICC fix's approach — judge each encoder quirk and allowlist it — does not survive this
 * one: an encoder-minted APP1 `Exif` is byte-for-byte the same KIND of segment as camera EXIF
 * carrying GPS, and any heuristic that waves one through (by size, by field count) is a
 * heuristic that can one day wave the other through. A GPS IFD fits in under 100 bytes.
 *
 * So the pipeline now does what the SERVER already does (`infra/src/upload/image-sanitizer.ts`
 * rebuilds from an allowlist, then self-checks): STRIP the whole metadata class from the
 * bytes we are about to store, whatever wrote it and whatever it says, and keep
 * {@link assertNoMetadata} as the self-check on the stripper's own output. Deleting a segment
 * cannot mis-parse its contents — there is no "is this one safe?" judgment left to get wrong.
 *
 * What is kept: APP0 (JFIF density header), APP2 `ICC_PROFILE` (a colour transform — the
 * rendering instruction the ICC note above already classified), every structural segment
 * (DQT/DHT/SOFn/DRI/RSTn), and the entropy-coded scan verbatim from SOS onward.
 * What is dropped: every other APPn (EXIF, XMP, IPTC/8BIM, maker notes) and COM.
 */
export function stripMetadataSegments(bytes: Uint8Array): Uint8Array {
  if (!isJpeg(bytes)) return bytes;
  const drop = findMetadataSegments(bytes);
  if (drop.length === 0) return bytes;

  const ranges = drop
    .map((segment) => ({
      start: segment.offset,
      // marker (2) + length field (2) + payload. RSTn/SOS never appear here — every
      // droppable segment has a length field by construction.
      end: segment.offset + 4 + segment.length,
    }))
    .sort((a, b) => a.start - b.start);

  const kept: Uint8Array[] = [];
  let cursor = 0;
  for (const range of ranges) {
    if (range.start > cursor) kept.push(bytes.subarray(cursor, range.start));
    cursor = range.end;
  }
  if (cursor < bytes.length) kept.push(bytes.subarray(cursor));

  const total = kept.reduce((sum, part) => sum + part.length, 0);
  const out = new Uint8Array(total);
  let offset = 0;
  for (const part of kept) {
    out.set(part, offset);
    offset += part.length;
  }
  return out;
}

export class MetadataNotStrippedError extends Error {
  constructor(readonly segments: JpegSegment[]) {
    super(
      `re-encoded JPEG still carries metadata: ${segments
        .map((s) => `${s.name}(${s.length}B "${s.head.slice(0, 12)}")`)
        .join(', ')}`,
    );
    this.name = 'MetadataNotStrippedError';
  }
}

export class NotJpegError extends Error {
  constructor() {
    super('re-encoded output is not a JPEG, so it cannot be checked for metadata');
    this.name = 'NotJpegError';
  }
}

/**
 * Throw unless the bytes are a JPEG with no EXIF/XMP/IPTC/comment segments.
 *
 * The `isJpeg` check is load-bearing, not defensive noise: `findMetadataSegments`
 * returns [] for anything that is not a JPEG, so without it this assertion would
 * silently pass for a PNG, a HEIC, or a truncated file — i.e. it would be a
 * safeguard that cannot fail, which is exactly the class of defect this project
 * keeps finding. If the output is not a JPEG we do not know what is in it.
 */
export function assertNoMetadata(bytes: Uint8Array): void {
  if (!isJpeg(bytes)) throw new NotJpegError();
  const found = findMetadataSegments(bytes);
  if (found.length > 0) throw new MetadataNotStrippedError(found);
}
